import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import InfoHint from "../../../../components/infoHint/InfoHint";
import Loader from "../../../../components/loader/Loader";
import SettingsPagesContainer from "../../../../components/settingsPagesContainer/SettingsPagesContainer";
import {
  KNOWN_PERMISSIONS,
  PERMISSION_DEPENDENTS,
  PERMISSION_LABELS,
  PERMISSION_SECTIONS,
  ROLE_BADGES,
  ROLE_DESCRIPTIONS,
  ROLE_DISPLAY_ORDER,
} from "../../../../constants/permissionCatalog";
import { isBuiltInRole, isTopRole } from "../../../../constants/roleRules";
import { createRoleAPI, deleteRoleAPI, fetchRolesAPI, updateRoleAPI } from "../../../../features/roles/rolesAPI";
import { showModal } from "../../../../features/statusModal/statusModal";
import DeleteRoleDialog from "./DeleteRoleDialog";
import LockGlyph from "./LockGlyph";
import ManageRolesEditor from "./ManageRolesEditor";
import RoleCard from "./RoleCard";
import RoleNameDialog from "./RoleNameDialog";
import SectionCard from "./SectionCard";
import styles from "./rolesPermissions.module.css";

// The app's revamp gradients (src/theme/gradients.js), as [from, to]. The
// top roles (Owner, Admin) get the brand sweep; the rest take these in order.
const TOP_ROLE_GRADIENT = ["var(--gradient-brand-from)", "var(--gradient-brand-to)"];
const ROLE_GRADIENTS = [
  ["var(--brand-amber)", "var(--brand-purple)"], // Linear/secondary/6
  ["var(--brand-purple)", "var(--brand-pink)"], // promoSeason
  ["var(--brand-green)", "var(--brand-cyan)"], // activePlan
  ["var(--brand-amber)", "var(--brand-green)"], // promoPrime
  ["var(--brand-blue)", "var(--brand-purple)"], // newTicketTechnical
  ["var(--brand-pink)", "var(--brand-purple)"], // squadAndFriends
  ["var(--brand-red)", "var(--brand-pink)"], // bugTechnical
  ["var(--brand-blue)", "var(--brand-cyan)"],
];

const roleIdOf = (role) => String(role?._id || "");
const sameSet = (a, b) => a.size === b.size && [...a].every((key) => b.has(key));

// Built-in roles first, in ROLE_DISPLAY_ORDER; the rest by name.
const rankOf = (name) => {
  const index = ROLE_DISPLAY_ORDER.indexOf(name);
  return index === -1 ? ROLE_DISPLAY_ORDER.length : index;
};
const sortRoles = (list) =>
  [...list].sort((a, b) => rankOf(a.role) - rankOf(b.role) || String(a.role).localeCompare(String(b.role)));

async function fetchSortedRoles() {
  const response = await fetchRolesAPI();
  return sortRoles(Array.isArray(response?.data) ? response.data : []);
}

// The backend's errorKey → what the page says (role.service, role routes).
const ROLE_ERRORS = {
  "errors.role.changedElsewhere": "عدّل شخص آخر هذا الدور للتو. تم تحميل آخر نسخة، راجع تغييراتك ثم احفظ مجدداً.",
  "errors.role.topRoleLocked": "هذا الدور يملك كل الصلاحيات ولا يُعدّل.",
  "errors.role.topRoleOnly": "تعديل الأدوار للأدمن والمالك فقط.",
  "errors.role.notFound": "هذا الدور لم يعد موجوداً.",
  "errors.role.nameTaken": "يوجد دور بهذا الاسم.",
  "errors.role.builtInLocked": "الأدوار الأساسية لا يتغير اسمها ولا تُحذف.",
  "errors.role.inUse": "لا يمكن حذف دور له موظفون. انقلهم إلى دور آخر أولاً.",
  "errors.role.unknownRoles": "بعض الأدوار المختارة لم تعد موجودة.",
  "errors.role.cannotManageTopRole": "لا يمكن إعطاء دور الأدمن أو المالك.",
  "errors.server.validation": "بعض القيم المرسلة غير صالحة.",
};
const errorKeyOf = (error) => error?.response?.data?.errorKey;
const roleErrorMessage = (error, fallback) => ROLE_ERRORS[errorKeyOf(error)] || fallback;

// A role made from the dashboard has no name-based behavior (backend
// config/roles.js): its admins reach only what is given to them directly.
const NEW_ROLE_HINT =
  "الدور الجديد يأخذ الصلاحيات فقط. موظفوه يصلون إلى البطولات المعطاة لهم من «الوصول إلى البطولات» وإلى المجموعات المسندة لهم، دون الوصول عبر التسلسل ودون ما تختص به الأدوار الأساسية. ولإعطائه لموظفين من غير الأدمن والمالك، أضفه إلى «يمكنه تعيين» في أدوارهم.";

const RolesPermissions = () => {
  const [roles, setRoles] = useState([]);
  const [status, setStatus] = useState("loading");
  const [selectedId, setSelectedId] = useState("");
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(false);
  // The open dialog: "create" | "copy" | "rename" | "delete" | "".
  const [dialog, setDialog] = useState("");
  const [dialogBusy, setDialogBusy] = useState(false);
  const [dialogError, setDialogError] = useState("");
  const [editingManage, setEditingManage] = useState(false);
  const [manageBusy, setManageBusy] = useState(false);
  const dispatch = useDispatch();
  // Only the top roles change roles (backend requireTopRole); anyone else
  // holding control-roles just looks.
  const { roleName: viewerRole } = useSelector((state) => state.user);
  const canEdit = isTopRole(viewerRole);

  useEffect(() => {
    let cancelled = false;
    fetchSortedRoles()
      .then((list) => {
        if (cancelled) return;
        setRoles(list);
        setSelectedId((current) => current || roleIdOf(list[0]));
        setStatus("succeeded");
      })
      .catch(() => {
        if (!cancelled) setStatus("failed");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Each role arrives with its admins (backend role list).
  const adminsByRole = useMemo(() => {
    if (!roles.some((role) => Array.isArray(role.admins))) return null;
    return Object.fromEntries(
      roles.map((role) => [roleIdOf(role), (role.admins || []).map((admin) => admin?.name || "")]),
    );
  }, [roles]);

  const gradients = useMemo(() => {
    const map = {};
    let next = 0;
    for (const role of roles) {
      map[roleIdOf(role)] = isTopRole(role.role)
        ? TOP_ROLE_GRADIENT
        : ROLE_GRADIENTS[next++ % ROLE_GRADIENTS.length];
    }
    return map;
  }, [roles]);

  const selectedRole = roles.find((role) => roleIdOf(role) === selectedId) || null;
  // The top roles hold every permission whatever their stored list says.
  const original = useMemo(
    () =>
      new Set(
        isTopRole(selectedRole?.role)
          ? KNOWN_PERMISSIONS
          : selectedRole?.permissions || [],
      ),
    [selectedRole],
  );
  const held = useMemo(
    () => (drafts[selectedId] ? new Set(drafts[selectedId]) : original),
    [drafts, selectedId, original],
  );
  const builtIn = isBuiltInRole(selectedRole?.role);
  const topRoleSelected = isTopRole(selectedRole?.role);
  const readOnly = topRoleSelected || !canEdit;
  const roleDescription = ROLE_DESCRIPTIONS[selectedRole?.role] || "";
  const [accentFrom, accentTo] = gradients[selectedId] || TOP_ROLE_GRADIENT;
  const accentStyle = { "--accent-from": accentFrom, "--accent-to": accentTo };

  const handleToggle = useCallback(
    (permission, nextOn) => {
      setDrafts((previous) => {
        const base = new Set(previous[selectedId] || original);
        if (nextOn) {
          base.add(permission);
        } else {
          base.delete(permission);
          for (const dependent of PERMISSION_DEPENDENTS[permission] || []) base.delete(dependent);
        }
        const next = { ...previous };
        if (sameSet(base, original)) delete next[selectedId];
        else next[selectedId] = [...base];
        return next;
      });
    },
    [selectedId, original],
  );

  const clearDraft = useCallback((roleId) => {
    setDrafts((previous) => {
      const next = { ...previous };
      delete next[roleId];
      return next;
    });
  }, []);
  const discard = () => clearDraft(selectedId);

  const added = [...held].filter((key) => !original.has(key));
  const removed = [...original].filter((key) => !held.has(key));
  const unknown = (selectedRole?.permissions || []).filter((key) => !KNOWN_PERMISSIONS.includes(key));
  const roleAdmins = adminsByRole?.[selectedId] || [];
  // Values from before the permission migration: saving would drop them
  // before the migration could turn them into today's permissions.
  const needsMigration = unknown.length > 0;
  // "يمكنه تعيين" offers every role but the top ones (the backend refuses those).
  const manageOptions = roles.map((role) => role.role).filter((name) => !isTopRole(name));

  // Saves the selected role's whole list. The version it was loaded at goes
  // along, so a newer edit from someone else is refused rather than undone.
  // The backend then reconnects that role's admins, whose dashboards re-read
  // their permissions.
  const save = async () => {
    const roleId = selectedId;
    const permissions = [...held];
    setSaving(true);
    try {
      const response = await updateRoleAPI(roleId, {
        permissions,
        expectedUpdatedAt: selectedRole.updatedAt,
      });
      const saved = response?.data || {};
      setRoles((list) => list.map((role) => (roleIdOf(role) === roleId ? { ...role, ...saved } : role)));
      clearDraft(roleId);
      dispatch(showModal({ message: "تم حفظ صلاحيات الدور", status: "success" }));
    } catch (error) {
      const key = error?.response?.data?.errorKey;
      if (key === "errors.role.changedElsewhere") {
        // The draft stays and now reads as changes against the newer version.
        try {
          const fresh = await fetchSortedRoles();
          setRoles(fresh);
          const latest = fresh.find((role) => roleIdOf(role) === roleId);
          if (latest && sameSet(new Set(permissions), new Set(latest.permissions || []))) clearDraft(roleId);
        } catch {
          // The message below still tells them to reload.
        }
      }
      dispatch(showModal({ message: ROLE_ERRORS[key] || "تعذر حفظ الدور", status: "error" }));
    } finally {
      setSaving(false);
    }
  };

  const selectRole = useCallback((roleId) => {
    setSelectedId(roleId);
    setEditingManage(false);
  }, []);
  const openDialog = (kind) => {
    setDialogError("");
    setDialog(kind);
  };
  const closeDialog = useCallback(() => setDialog(""), []);

  // Runs one dialog request: busy while it runs; a failure stays in the dialog.
  const runDialog = async (request, fallbackError) => {
    setDialogBusy(true);
    setDialogError("");
    try {
      await request();
      setDialog("");
    } catch (error) {
      setDialogError(roleErrorMessage(error, fallbackError));
      if (errorKeyOf(error) === "errors.role.changedElsewhere") {
        setRoles(await fetchSortedRoles().catch(() => roles));
      }
    } finally {
      setDialogBusy(false);
    }
  };

  // A new role starts empty; a copy takes what the source shows now (its
  // unsaved changes included) and its "يمكنه تعيين".
  const createRole = (name) =>
    runDialog(async () => {
      const copying = dialog === "copy";
      const roleNames = new Set(roles.map((role) => role.role));
      const response = await createRoleAPI({
        role: name,
        permissions: copying ? [...held].filter((key) => KNOWN_PERMISSIONS.includes(key)) : [],
        canManageRoles: copying
          ? (selectedRole.canManageRoles || []).filter((other) => roleNames.has(other) && !isTopRole(other))
          : [],
      });
      const created = { ...response.data, admins: [] };
      setRoles((list) => sortRoles([...list, created]));
      selectRole(roleIdOf(created));
      dispatch(showModal({ message: copying ? "تم نسخ الدور" : "تمت إضافة الدور", status: "success" }));
    }, "تعذر إضافة الدور");

  // Other roles' "يمكنه تعيين" carry the new name, so the list is reloaded.
  const renameRole = (name) =>
    runDialog(async () => {
      await updateRoleAPI(selectedId, { role: name, expectedUpdatedAt: selectedRole.updatedAt });
      setRoles(await fetchSortedRoles());
      dispatch(showModal({ message: "تم تغيير اسم الدور", status: "success" }));
    }, "تعذر تغيير الاسم");

  const deleteRole = () =>
    runDialog(async () => {
      const roleId = selectedId;
      await deleteRoleAPI(roleId);
      const fresh = await fetchSortedRoles();
      setRoles(fresh);
      clearDraft(roleId);
      selectRole(roleIdOf(fresh[0]));
      dispatch(showModal({ message: "تم حذف الدور", status: "success" }));
    }, "تعذر حذف الدور");

  const saveManagedRoles = async (names) => {
    const roleId = selectedId;
    setManageBusy(true);
    try {
      const response = await updateRoleAPI(roleId, {
        canManageRoles: names,
        expectedUpdatedAt: selectedRole.updatedAt,
      });
      const saved = response?.data || {};
      setRoles((list) => list.map((role) => (roleIdOf(role) === roleId ? { ...role, ...saved } : role)));
      setEditingManage(false);
      dispatch(showModal({ message: "تم حفظ الأدوار التي يمكنه تعيينها", status: "success" }));
    } catch (error) {
      if (errorKeyOf(error) === "errors.role.changedElsewhere") {
        setRoles(await fetchSortedRoles().catch(() => roles));
        setEditingManage(false);
      }
      dispatch(showModal({ message: roleErrorMessage(error, "تعذر الحفظ"), status: "error" }));
    } finally {
      setManageBusy(false);
    }
  };

  if (status === "loading") {
    return (
      <SettingsPagesContainer title="الأدوار والصلاحيات" fullWidth>
        <div className={styles.centered}>
          <Loader />
        </div>
      </SettingsPagesContainer>
    );
  }

  if (status === "failed") {
    return (
      <SettingsPagesContainer title="الأدوار والصلاحيات" fullWidth>
        <div className={styles.centered}>تعذر جلب الأدوار.</div>
      </SettingsPagesContainer>
    );
  }

  return (
    <SettingsPagesContainer
      title="الأدوار والصلاحيات"
      fullWidth
      displayContainerClassName={styles.display}
    >
      <div className={styles.page} dir="rtl">
        <div className={styles.roleGrid}>
          {roles.map((role) => {
            const id = roleIdOf(role);
            return (
              <RoleCard
                key={id}
                roleId={id}
                name={role.role}
                description={ROLE_DESCRIPTIONS[role.role] || ""}
                accentFrom={gradients[id][0]}
                accentTo={gradients[id][1]}
                adminCount={adminsByRole ? (adminsByRole[id] || []).length : null}
                permissionCount={
                  isTopRole(role.role)
                    ? KNOWN_PERMISSIONS.length
                    : (drafts[id] || role.permissions || []).filter((key) => KNOWN_PERMISSIONS.includes(key)).length
                }
                totalPermissions={KNOWN_PERMISSIONS.length}
                isLocked={isBuiltInRole(role.role)}
                badge={ROLE_BADGES[role.role] || ""}
                isSelected={id === selectedId}
                hasChanges={Boolean(drafts[id])}
                onSelect={selectRole}
              />
            );
          })}
          {canEdit && (
            <button type="button" className={styles.newRoleCard} onClick={() => openDialog("create")}>
              <span className={styles.newRolePlus}>+</span>
              دور جديد
            </button>
          )}
        </div>

        {selectedRole && (
          <>
            <div className={styles.detailHeader} style={accentStyle}>
              <div className={styles.detailIdentity}>
                <span className={styles.detailAvatar}>{String(selectedRole.role).slice(0, 1).toUpperCase()}</span>
                <div className={styles.detailText}>
                  <div className={styles.detailNameRow}>
                    <span className={styles.detailName} dir="ltr">
                      {selectedRole.role}
                    </span>
                    {builtIn ? (
                      <span className={styles.detailLock}>
                        <LockGlyph className={styles.lockGlyph} />
                        {topRoleSelected ? "للعرض فقط" : "اسم ثابت"}
                      </span>
                    ) : (
                      canEdit && (
                        <button type="button" className={styles.inlineBtn} onClick={() => openDialog("rename")}>
                          تغيير الاسم
                        </button>
                      )
                    )}
                  </div>
                  {roleDescription && (
                    <span className={styles.detailDescription}>{roleDescription}</span>
                  )}
                </div>
              </div>
              {canEdit && (
                <div className={styles.detailActions}>
                  <button
                    type="button"
                    className={styles.ghostBtn}
                    onClick={() => openDialog("copy")}
                    disabled={needsMigration}
                    title={needsMigration ? "شغّل سكربت ترحيل الصلاحيات أولاً" : undefined}
                  >
                    نسخ كدور جديد
                  </button>
                  <button
                    type="button"
                    className={styles.dangerGhostBtn}
                    onClick={() => openDialog("delete")}
                    disabled={builtIn || roleAdmins.length > 0}
                    title={
                      builtIn
                        ? "الأدوار الأساسية لا تُحذف"
                        : roleAdmins.length > 0
                          ? `انقل موظفيه (${roleAdmins.length}) إلى دور آخر أولاً`
                          : undefined
                    }
                  >
                    حذف الدور
                  </button>
                </div>
              )}
              <div className={styles.detailFacts}>
                <div className={styles.factBlock}>
                  <span className={styles.factLabel}>الموظفون ({roleAdmins.length})</span>
                  <div className={styles.chips}>
                    {roleAdmins.length === 0 ? (
                      <span className={styles.chipMuted}>لا يوجد</span>
                    ) : (
                      roleAdmins.map((name, index) => (
                        <span key={`${name}-${index}`} className={styles.chip} dir="auto">
                          {name}
                        </span>
                      ))
                    )}
                  </div>
                </div>
                <div className={styles.factBlock}>
                  <div className={styles.factHeader}>
                    <span className={styles.factLabel}>
                      يمكنه تعيين
                      <InfoHint
                        text="الأدوار التي يستطيع موظفو هذا الدور إعطاءها عند إضافة موظف أو تغيير دوره."
                        label="توضيح: يمكنه تعيين"
                      />
                    </span>
                    {!readOnly && !editingManage && (
                      <button type="button" className={styles.inlineBtn} onClick={() => setEditingManage(true)}>
                        تعديل
                      </button>
                    )}
                  </div>
                  {topRoleSelected ? (
                    <div className={styles.chips}>
                      <span className={styles.chipMuted}>كل الأدوار</span>
                    </div>
                  ) : editingManage ? (
                    <ManageRolesEditor
                      options={manageOptions}
                      selected={selectedRole.canManageRoles || []}
                      busy={manageBusy}
                      onSave={saveManagedRoles}
                      onCancel={() => setEditingManage(false)}
                    />
                  ) : (
                    <div className={styles.chips}>
                      {(selectedRole.canManageRoles || []).length === 0 ? (
                        <span className={styles.chipMuted}>لا أحد</span>
                      ) : (
                        selectedRole.canManageRoles.map((name) => (
                          <span key={name} className={styles.chipOutline} dir="ltr">
                            {name}
                          </span>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {topRoleSelected ? (
              <div className={styles.topRoleNote}>
                هذا الدور يملك كل الصلاحيات دائماً ولا يُعدّل من هنا.
              </div>
            ) : (
              !canEdit && <div className={styles.topRoleNote}>للعرض فقط: تعديل الأدوار للأدمن والمالك.</div>
            )}

            <div className={styles.sectionGrid}>
              {PERMISSION_SECTIONS.map((section) => (
                <SectionCard
                  key={section.id}
                  section={section}
                  held={held}
                  readOnly={readOnly || saving}
                  accentFrom={accentFrom}
                  accentTo={accentTo}
                  onToggle={handleToggle}
                />
              ))}

              {unknown.length > 0 && (
                <section className={styles.sectionCard} style={accentStyle}>
                  <header className={styles.sectionHeader}>
                    <div className={styles.sectionTitleBlock}>
                      <span className={styles.sectionTitle}>قيم غير معروفة</span>
                      <span className={styles.sectionMeta}>
                        موجودة في قاعدة البيانات لكنها ليست في قائمة الصلاحيات. شغّل سكربت ترحيل الصلاحيات قبل تعديل هذا الدور.
                      </span>
                    </div>
                  </header>
                  <div className={styles.chips}>
                    {unknown.map((key) => (
                      <span key={key} className={styles.chipOutline} dir="ltr">
                        {key}
                      </span>
                    ))}
                  </div>
                </section>
              )}
            </div>

            {(added.length > 0 || removed.length > 0) && (
              <div className={styles.changesBar}>
                <div className={styles.changesSummary}>
                  <span className={styles.changesCount}>{added.length + removed.length} تغيير</span>
                  <div className={styles.changesChips}>
                    {added.map((key) => (
                      <span key={`+${key}`} className={styles.chipAdded}>
                        + {PERMISSION_LABELS[key] || key}
                      </span>
                    ))}
                    {removed.map((key) => (
                      <span key={`-${key}`} className={styles.chipRemoved}>
                        − {PERMISSION_LABELS[key] || key}
                      </span>
                    ))}
                  </div>
                </div>
                <div className={styles.changesActions}>
                  <span className={styles.changesAffects}>يؤثر على {roleAdmins.length} موظف</span>
                  <button type="button" className={styles.ghostBtn} onClick={discard} disabled={saving}>
                    تراجع
                  </button>
                  <button
                    type="button"
                    className={styles.saveBtn}
                    onClick={save}
                    disabled={saving || needsMigration}
                    title={needsMigration ? "شغّل سكربت ترحيل الصلاحيات أولاً" : undefined}
                  >
                    {saving ? "جارٍ الحفظ…" : "حفظ"}
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {(dialog === "create" || dialog === "copy") && (
          <RoleNameDialog
            title={dialog === "copy" ? `نسخ ${selectedRole?.role} كدور جديد` : "دور جديد"}
            hint={
              dialog === "copy"
                ? `يأخذ صلاحيات ${selectedRole?.role} الظاهرة الآن و«يمكنه تعيين». ${NEW_ROLE_HINT}`
                : NEW_ROLE_HINT
            }
            initialName={dialog === "copy" ? `${selectedRole?.role}-Copy` : ""}
            currentName=""
            submitLabel={dialog === "copy" ? "نسخ" : "إضافة"}
            busy={dialogBusy}
            error={dialogError}
            onSubmit={createRole}
            onClose={closeDialog}
          />
        )}
        {dialog === "rename" && selectedRole && (
          <RoleNameDialog
            title="تغيير اسم الدور"
            hint="الاسم الجديد يحل محل القديم في «يمكنه تعيين» في كل الأدوار."
            initialName={selectedRole.role}
            currentName={selectedRole.role}
            submitLabel="حفظ"
            busy={dialogBusy}
            error={dialogError}
            onSubmit={renameRole}
            onClose={closeDialog}
          />
        )}
        {dialog === "delete" && selectedRole && (
          <DeleteRoleDialog
            roleName={selectedRole.role}
            busy={dialogBusy}
            error={dialogError}
            onConfirm={deleteRole}
            onClose={closeDialog}
          />
        )}
      </div>
    </SettingsPagesContainer>
  );
};

export default RolesPermissions;
